import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ClipboardCheck, Download, ExternalLink, Plus, Search, Trash2, UserRoundCheck } from 'lucide-react'
import { PeriodSelector } from '@/components/analytics'
import { Button, Card } from '@/components/ui'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { withAuthRecovery } from '@/lib/authRecovery'
import { useAuthStore, useStudioStore } from '@/store'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { getSupabase } from '@/lib/supabase'
import {
  createTrainerChecklistReviewLink,
  formatTrainerReviewDateTime,
  getTrainerChecklistMonthLabel,
  openTrainerChecklistReport,
} from '@/lib/trainerChecklistReviews'
import { formatNumber, type MetricPeriod } from '@/lib/utils'
import { formatPedagogySystemTimestamp } from '@/lib/pedagogyTime'
import {
  archiveTrainerReview,
  listAllTrainerReviewSummary,
  listMyTrainerReviewSummary,
  type TrainerReviewSummaryRow,
} from '@/services/trainerReviewService'

function formatDateTime(dateString: string): string {
  return formatTrainerReviewDateTime(dateString) || '-'
}

function formatDateTimeFromTimestamp(value: string): string {
  const formatted = formatPedagogySystemTimestamp(value)
  return formatted === '—' ? '-' : formatted
}

function formatDateOnly(value: string): string {
  if (!value) return '—'
  const date = new Date(`${value.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU')
}

function formatPercent(value: number): string {
  return `${value.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`
}

function getPercentTone(value: number): string {
  if (value > 80) return 'text-success'
  if (value >= 50) return 'text-amber-700'
  return 'text-error'
}

function getMonthKey(review: TrainerReviewSummaryRow): string {
  const sourceDate = review.reviewDate || review.createdAt.slice(0, 10)
  return sourceDate.slice(0, 7) || 'no-date'
}

function getMonthSortValue(monthKey: string): number {
  if (monthKey === 'no-date') return 0
  return Number(monthKey.replace('-', ''))
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getReviewPeriodDate(review: TrainerReviewSummaryRow): string {
  return review.reviewDate || review.createdAt.slice(0, 10)
}

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100] as const

export function TrainerChecklistSummaryPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const periodIdParam = searchParams.get('periodId') ?? ''
  // Неделя из URL читается один раз при монтировании: дальше источник истины —
  // сам селект, а перечитывание из `searchParams` внутри эффекта сделало бы его
  // зависимым от каждого изменения адреса.
  const initialWeekRef = useRef(searchParams.get('week') ?? '')
  const weekRestoredRef = useRef(false)
  const { user } = useAuthStore()
  const { studios, partners } = useStudioStore()
  const { canManagePedagogy, isPedagogySpecialist } = usePedagogyAccess()
  const [periods, setPeriods] = useState<MetricPeriod[]>([])
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null)
  const [reviews, setReviews] = useState<TrainerReviewSummaryRow[]>([])
  /**
   * Всё состояние фильтрации живёт в URL (образец — `PedagogyTeachersPage`).
   * До 21.08.2026 в URL были только `reviewerId`/`periodId`/`teacherId`, поэтому
   * типовой сценарий «пройти все отсмотры ниже 50% по городу N» заставлял после
   * каждого возврата из карточки заново выставлять 3–4 селекта и долистывать до
   * нужной страницы, а ссылкой на отфильтрованный список поделиться было нельзя.
   */
  const cityFilter = searchParams.get('city') ?? 'all'
  const partnerFilter = searchParams.get('partner') ?? 'all'
  const studioFilter = searchParams.get('studio') ?? 'all'
  const coachFilter = searchParams.get('coach') ?? 'all'
  const directionFilter = searchParams.get('direction') ?? 'all'
  const qualityFilter = searchParams.get('quality') ?? 'all'
  const sourceFilter = searchParams.get('source') ?? 'all'
  const search = searchParams.get('q') ?? ''
  // В обычном режиме специалист сохраняет свой персональный реестр. Если экран
  // открыт из карточки педагога (`teacherId`), сервер отдаёт официальный read-only
  // контур текущих франчайзи, поэтому фильтр по автору должен быть снят.
  const reviewerFilter = isPedagogySpecialist
    ? (user?.id ?? 'all')
    : (searchParams.get('reviewerId') ?? 'all')
  const teacherFilter = searchParams.get('teacherId') || ''
  const teacherFilterName = searchParams.get('teacherName') || ''
  const teacherTimeScope: 'all' | 'period' = teacherFilter && searchParams.get('teacherScope') === 'period' ? 'period' : 'all'
  const sourceReturnTo = searchParams.get('returnTo') || ''
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [archiveMessage, setArchiveMessage] = useState('')
  const [archivingReviewId, setArchivingReviewId] = useState<string | null>(null)
  const pageSize = PAGE_SIZE_OPTIONS.includes(Number(searchParams.get('pageSize')) as typeof PAGE_SIZE_OPTIONS[number])
    ? Number(searchParams.get('pageSize'))
    : 20
  const page = Math.max(1, Number(searchParams.get('page')) || 1)
  const returnTo = `${location.pathname}${location.search}`
  // Поле поиска держит собственное состояние и уезжает в URL с дебаунсом:
  // гонять каждый символ через роутер значит навешивать навигацию на набор.
  const [searchInput, setSearchInput] = useState(search)

  /**
   * Любая смена фильтра сбрасывает страницу — но именно здесь, в сеттере, а не
   * эффектом по изменению фильтров: эффект отработал бы и на монтировании и
   * стёр бы `page` из присланной коллегой ссылки.
   */
  const setUrlFilter = useCallback((key: string, value: string, defaultValue = 'all') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (!value || value === defaultValue) next.delete(key)
      else next.set(key, value)
      if (key !== 'page') next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  // Ref хранит значение, на котором поле и URL в последний раз сошлись, —
  // по нему различаются «человек печатает» и «адрес поменяли снаружи»
  // (Back/Forward, приход по ссылке). Без этого поле с прежним текстом
  // затирало бы `q`, только что пришедший из истории.
  const syncedSearchRef = useRef(search)

  useEffect(() => {
    if (search === syncedSearchRef.current) return
    syncedSearchRef.current = search
    setSearchInput(search)
  }, [search])

  useEffect(() => {
    if (searchInput === syncedSearchRef.current) return
    const timeoutId = window.setTimeout(() => {
      const normalized = searchInput.trim()
      syncedSearchRef.current = normalized
      if (normalized !== search) setUrlFilter('q', normalized, '')
    }, 250)
    return () => window.clearTimeout(timeoutId)
  }, [search, searchInput, setUrlFilter])

  const selectedPeriod = useMemo(
    () => periods.find(period => period.period_id === selectedPeriodId) ?? null,
    [periods, selectedPeriodId]
  )

  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    timeFilterOptions,
    selectedRange,
  } = useAnalyticsTimeFilter(selectedPeriod, { isCurrentPeriod: true })

  useEffect(() => {
    let cancelled = false

    async function loadPeriods() {
      const supabase = getSupabase()
      const { data, error: periodsError } = await withAuthRecovery(() =>
        supabase.from('metric_periods').select('*').order('period_start', { ascending: false })
      )
      if (periodsError) throw periodsError
      if (cancelled) return

      const gateDate = new Date()
      gateDate.setDate(gateDate.getDate() + 3)
      const openUntil = formatIsoDate(gateDate)
      const today = formatIsoDate(new Date())
      const nextPeriods = ((data ?? []) as MetricPeriod[]).filter(period => period.period_start <= openUntil)
      setPeriods(nextPeriods)

      // Неделю из URL применяем ровно здесь, одной пачкой с периодом: сам хук
      // сбрасывает выбор в «Период целиком», пока список недель ещё пуст, —
      // то есть восстановление на монтировании было бы стёрто его же гвардом.
      if (!weekRestoredRef.current) {
        weekRestoredRef.current = true
        const weekParam = initialWeekRef.current
        if (/^week_[1-9]\d*$/.test(weekParam)) setSelectedTimeFilter(weekParam)
      }

      const searchPeriodId = Number(periodIdParam || '')
      if (Number.isFinite(searchPeriodId) && nextPeriods.some(period => period.period_id === searchPeriodId)) {
        setSelectedPeriodId(searchPeriodId)
        return
      }

      const current = nextPeriods.find(period => period.period_start <= today && today <= period.period_end) ?? nextPeriods[0] ?? null
      setSelectedPeriodId(current?.period_id ?? null)
    }

    void loadPeriods().catch(loadError => {
      if (cancelled) return
      console.error('Failed to load trainer review periods:', loadError)
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить периоды')
    })

    return () => {
      cancelled = true
    }
    // Зависимость — только `periodId`, а не весь `searchParams`: иначе каждый
    // символ в поиске и каждое переключение селекта дёргали бы `metric_periods`.
    // `setSelectedTimeFilter` — стабильный сеттер `useState`, перезапуска не даёт.
  }, [periodIdParam, setSelectedTimeFilter])

  useEffect(() => {
    let cancelled = false

    async function loadReviews() {
      if (isPedagogySpecialist && !user?.id) return
      setLoading(true)
      setError('')

      try {
        // Обычный вход специалиста остаётся персональным: там смешаны его
        // официальные и контрольные результаты, поэтому используется отдельная
        // закрытая RPC. Из карточки педагога нужен другой сценарий: показать
        // официальную историю, которую специалист может читать по CURRENT
        // франчайзи-скоупу. Этот серверный read-контур не выдаёт контрольные
        // результаты и не даёт прав редактирования чужих отсмотров.
        const rows = isPedagogySpecialist
          ? (teacherFilter
            ? await listAllTrainerReviewSummary({ reviewerUserId: null })
            : await listMyTrainerReviewSummary(user?.id ?? ''))
          : await listAllTrainerReviewSummary({ reviewerUserId: null })
        if (cancelled) return
        setReviews(rows)
      } catch (loadError) {
        if (cancelled) return
        console.error('Failed to load trainer review summary:', loadError)
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить сводку')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadReviews()
    return () => {
      cancelled = true
    }
  }, [isPedagogySpecialist, teacherFilter, user?.id])

  useEffect(() => {
    if (selectedPeriodId == null) return
    const next = new URLSearchParams(searchParams)

    if (isPedagogySpecialist) next.delete('reviewerId')

    if (teacherFilter && teacherTimeScope === 'all') {
      next.delete('periodId')
    } else {
      next.set('periodId', String(selectedPeriodId))
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [isPedagogySpecialist, searchParams, selectedPeriodId, setSearchParams, teacherFilter, teacherTimeScope])

  const reviewerOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const review of reviews) {
      const reviewerId = review.reviewerUserId.trim()
      const reviewerName = review.reviewerDisplayName.trim()
      if (!reviewerId || !reviewerName || seen.has(reviewerId)) continue
      seen.set(reviewerId, reviewerName)
    }

    return Array.from(seen.entries())
      .sort(([, leftName], [, rightName]) => leftName.localeCompare(rightName, 'ru'))
      .map(([value, label]) => ({ value, label }))
  }, [reviews])

  const cityOptions = useMemo(() => {
    return Array.from(new Set(reviews.map(review => review.cityName.trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'ru'))
      .map(value => ({ value, label: value }))
  }, [reviews])

  const studioOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const review of reviews) {
      if (review.studioInternalId == null) continue
      const studioMeta = studios.find(studio => studio.studio_internal_id === review.studioInternalId)
      const studioNumber = studioMeta?.studio_number ? `Студия ${studioMeta.studio_number}` : `ID ${review.studioInternalId}`
      const label = `${studioNumber}${review.cityName ? ` · ${review.cityName}` : ''}`
      seen.set(String(review.studioInternalId), label)
    }

    return Array.from(seen.entries())
      .sort(([, left], [, right]) => left.localeCompare(right, 'ru'))
      .map(([value, label]) => ({ value, label }))
  }, [reviews, studios])

  const partnerOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const review of reviews) {
      if (review.studioInternalId == null) continue
      const studioMeta = studios.find(studio => studio.studio_internal_id === review.studioInternalId)
      const partnerNumber = studioMeta?.partner_number
      if (partnerNumber == null) continue
      const key = String(partnerNumber)
      if (!seen.has(key)) {
        const partnerMeta = partners.find(partner => partner.partner_number === partnerNumber)
        seen.set(key, partnerMeta?.label || partnerMeta?.persons_fio || `Франчайзи #${partnerNumber}`)
      }
    }

    return Array.from(seen.entries())
      .sort(([, left], [, right]) => left.localeCompare(right, 'ru'))
      .map(([value, label]) => ({ value, label }))
  }, [partners, reviews, studios])

  const coachOptions = useMemo(() => {
    return Array.from(new Set(reviews.map(review => review.coachName.trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'ru'))
      .map(value => ({ value, label: value }))
  }, [reviews])

  const directionOptions = useMemo(() => {
    return Array.from(new Set(reviews.map(review => review.directionName.trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'ru'))
      .map(value => ({ value, label: value }))
  }, [reviews])

  const effectiveReviewerFilter = isPedagogySpecialist
    ? (teacherFilter ? 'all' : (user?.id ?? '__missing_user__'))
    : reviewerFilter

  const filteredReviews = useMemo(() => {
    const query = search.trim().toLowerCase()

    return reviews
      .filter(review => {
        if (teacherFilter && teacherTimeScope === 'all') return true
        if (!selectedRange) return true
        const reviewDate = getReviewPeriodDate(review)
        return reviewDate >= selectedRange.start && reviewDate <= selectedRange.end
      })
      .filter(review => effectiveReviewerFilter === 'all' || review.reviewerUserId === effectiveReviewerFilter)
      .filter(review => !teacherFilter || review.teacherId === teacherFilter)
      .filter(review => cityFilter === 'all' || review.cityName === cityFilter)
      .filter(review => studioFilter === 'all' || String(review.studioInternalId ?? '') === studioFilter)
      .filter(review => coachFilter === 'all' || review.coachName.trim() === coachFilter)
      .filter(review => directionFilter === 'all' || review.directionName.trim() === directionFilter)
      .filter(review => sourceFilter === 'all' || review.reviewSource === sourceFilter)
      .filter(review => {
        if (partnerFilter === 'all') return true
        if (review.studioInternalId == null) return false
        const studioMeta = studios.find(studio => studio.studio_internal_id === review.studioInternalId)
        return String(studioMeta?.partner_number ?? '') === partnerFilter
      })
      .filter(review => {
        if (qualityFilter === 'all') return true
        if (qualityFilter === 'high') return review.qualityPercent > 80
        if (qualityFilter === 'medium') return review.qualityPercent >= 50 && review.qualityPercent <= 80
        return review.qualityPercent < 50
      })
      .filter(review => {
        if (!query) return true

        return [
          review.studioAddress,
          review.cityName,
          review.coachName,
          review.studioAddress,
          review.directionName,
          review.reviewerDisplayName,
        ].some(value => value.toLowerCase().includes(query))
      })
      .sort((a, b) => {
        const dateDiff = (b.createdAt || '').localeCompare(a.createdAt || '')
        if (dateDiff !== 0) return dateDiff
        return b.updatedAt.localeCompare(a.updatedAt)
      })
  }, [cityFilter, coachFilter, directionFilter, effectiveReviewerFilter, partnerFilter, qualityFilter, reviews, search, selectedRange, sourceFilter, studioFilter, studios, teacherFilter, teacherTimeScope])

  const lowScoreCount = filteredReviews.filter(review => review.qualityPercent < 50).length
  const uniqueCoaches = new Set(filteredReviews.map(review => review.coachName.trim()).filter(Boolean)).size
  const latestFilteredReviewDate = useMemo(() => {
    const dates = filteredReviews.map(getReviewPeriodDate).filter(Boolean).sort()
    return dates.length > 0 ? dates[dates.length - 1] : ''
  }, [filteredReviews])

  const pageCount = Math.max(1, Math.ceil(filteredReviews.length / pageSize))
  const safePage = Math.min(page, pageCount)
  const pagedReviews = useMemo(() => {
    const startIndex = (safePage - 1) * pageSize
    return filteredReviews.slice(startIndex, startIndex + pageSize)
  }, [filteredReviews, pageSize, safePage])

  const pagedGroupedReviews = useMemo(() => {
    const groups = new Map<string, TrainerReviewSummaryRow[]>()
    for (const review of pagedReviews) {
      const monthKey = getMonthKey(review)
      groups.set(monthKey, [...(groups.get(monthKey) ?? []), review])
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => getMonthSortValue(b) - getMonthSortValue(a))
      .map(([monthKey, rows]) => ({
        monthKey,
        monthLabel: monthKey === 'no-date' ? 'Без даты' : getTrainerChecklistMonthLabel(`${monthKey}-01`),
        rows,
      }))
  }, [pagedReviews])

  const selectedPeriodIndex = useMemo(
    () => periods.findIndex(period => period.period_id === selectedPeriodId),
    [periods, selectedPeriodId]
  )

  const canGoToNewerPeriod = selectedPeriodIndex > 0
  const canGoToOlderPeriod = selectedPeriodIndex >= 0 && selectedPeriodIndex < periods.length - 1

  const handleArchiveReview = async (review: TrainerReviewSummaryRow) => {
    if (!canManagePedagogy || archivingReviewId) return

    const coachName = review.coachName.trim() || 'Без педагога'
    const reviewDate = formatTrainerReviewDateTime(review.trainingAt) || review.reviewDate || 'без даты'
    const confirmed = window.confirm(
      `Удалить отсмотр педагога «${coachName}» от ${reviewDate}?\n\n` +
      'Запись будет перемещена в архив и перестанет учитываться в показателях.'
    )
    if (!confirmed) return

    setArchivingReviewId(review.reviewId)
    setArchiveMessage('')
    setError('')

    try {
      await archiveTrainerReview(review.reviewId)
      setReviews(current => current.filter(item => item.reviewId !== review.reviewId))
      setArchiveMessage('Отсмотр перемещён в архив и исключён из показателей.')
    } catch (archiveError) {
      console.error('Failed to archive trainer review:', archiveError)
      setError(archiveError instanceof Error ? archiveError.message : 'Не удалось удалить отсмотр')
    } finally {
      setArchivingReviewId(null)
    }
  }

  const clearTeacherFilter = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('teacherId')
    next.delete('teacherName')
    next.delete('teacherScope')
    next.delete('page')
    setSearchParams(next)
  }

  const setTeacherTimeScope = (scope: 'all' | 'period') => {
    const next = new URLSearchParams(searchParams)
    if (scope === 'period') {
      next.set('teacherScope', 'period')
    } else {
      next.delete('teacherScope')
      next.delete('periodId')
    }
    next.delete('page')
    setSearchParams(next)
  }

  return (
    <div className="space-y-4 pb-8">
      {teacherFilter && (
        <Card className="border border-primary/20 bg-primary/5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 text-sm">
              <span className="font-medium">Педагог:</span>{' '}
              <span className="font-semibold">{teacherFilterName || `ID ${teacherFilter}`}</span>
              <span className="ml-2 text-muted-foreground">· {formatNumber(filteredReviews.length)} отсмотров</span>
              {isPedagogySpecialist && (
                <span className="ml-2 text-muted-foreground">· только ваши отсмотры</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-lg border border-border bg-background p-0.5">
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-xs font-medium ${teacherTimeScope === 'all' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setTeacherTimeScope('all')}
                >
                  За всё время
                </button>
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-xs font-medium ${teacherTimeScope === 'period' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setTeacherTimeScope('period')}
                >
                  За период
                </button>
              </div>
              {sourceReturnTo && (
                <button type="button" className="text-sm text-primary" onClick={() => navigate(sourceReturnTo)}>
                  Вернуться в карточку
                </button>
              )}
              <button type="button" className="text-sm text-primary" onClick={clearTeacherFilter}>
                Убрать фильтр ×
              </button>
            </div>
          </div>
        </Card>
      )}

      {(!teacherFilter || teacherTimeScope === 'period') && (
        <PeriodSelector
          currentPeriod={selectedPeriod}
          onPrevPeriod={() => {
            if (!canGoToOlderPeriod) return
            setSelectedPeriodId(periods[selectedPeriodIndex + 1]?.period_id ?? null)
            setUrlFilter('page', '1', '1')
          }}
          onNextPeriod={() => {
            if (!canGoToNewerPeriod) return
            setSelectedPeriodId(periods[selectedPeriodIndex - 1]?.period_id ?? null)
            setUrlFilter('page', '1', '1')
          }}
          canGoPrev={canGoToOlderPeriod}
          canGoNext={canGoToNewerPeriod}
          showViewModeSelector={true}
          isCurrentPeriod={true}
          periodFilterValue={selectedTimeFilter}
          periodFilterOptions={timeFilterOptions}
          onPeriodFilterChange={value => {
            setSelectedTimeFilter(value)
            setUrlFilter('week', value, 'current_period')
          }}
          selectedRange={selectedRange}
        />
      )}

      {error && (
        <Card className="border border-error/30 bg-error/5 shadow-sm">
          <div className="text-sm text-error">{error}</div>
        </Card>
      )}

      {archiveMessage && (
        <Card className="border border-success/30 bg-success/5 shadow-sm">
          <div className="text-sm text-success">{archiveMessage}</div>
        </Card>
      )}

      <Card className="border border-border shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3 border-b border-border pb-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <UserRoundCheck className="h-4 w-4 text-muted-foreground" />
            {teacherFilter ? 'Отсмотры педагога' : (isPedagogySpecialist ? 'Мои отсмотры' : 'Все отсмотры')}
          </div>
          <Button
            type="button"
            size="sm"
            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap"
            onClick={() => navigate(`${pedagogyPaths.reviewNew}${teacherFilter ? `?teacherId=${encodeURIComponent(teacherFilter)}&returnTo=${encodeURIComponent(returnTo)}` : ''}`)}
          >
            <Plus className="mr-1.5 inline h-4 w-4" />
            Новый отсмотр
          </Button>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {!isPedagogySpecialist && (
            <label className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">Специалист</span>
              <select
                value={reviewerFilter}
                onChange={event => setUrlFilter('reviewerId', event.target.value)}
                className="input w-full"
              >
                <option value="all">Все</option>
                {reviewerOptions.map(reviewer => (
                  <option key={reviewer.value} value={reviewer.value}>{reviewer.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-muted-foreground">Город</span>
            <select
              value={cityFilter}
              onChange={event => setUrlFilter('city', event.target.value)}
              className="input w-full"
            >
              <option value="all">Все</option>
              {cityOptions.map(city => (
                <option key={city.value} value={city.value}>{city.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-muted-foreground">Франчайзи</span>
            <select
              value={partnerFilter}
              onChange={event => setUrlFilter('partner', event.target.value)}
              className="input w-full"
            >
              <option value="all">Все</option>
              {partnerOptions.map(partner => (
                <option key={partner.value} value={partner.value}>{partner.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-muted-foreground">Студия</span>
            <select
              value={studioFilter}
              onChange={event => setUrlFilter('studio', event.target.value)}
              className="input w-full"
            >
              <option value="all">Все</option>
              {studioOptions.map(studio => (
                <option key={studio.value} value={studio.value}>{studio.label}</option>
              ))}
            </select>
          </label>
          {!teacherFilter && (
            <label className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">Педагог</span>
              <select
                value={coachFilter}
                onChange={event => setUrlFilter('coach', event.target.value)}
                className="input w-full"
              >
                <option value="all">Все</option>
                {coachOptions.map(coach => (
                  <option key={coach.value} value={coach.value}>{coach.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-muted-foreground">Направление</span>
            <select
              value={directionFilter}
              onChange={event => setUrlFilter('direction', event.target.value)}
              className="input w-full"
            >
              <option value="all">Все</option>
              {directionOptions.map(direction => (
                <option key={direction.value} value={direction.value}>{direction.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-muted-foreground">Источник</span>
            <select
              value={sourceFilter}
              onChange={event => setUrlFilter('source', event.target.value)}
              className="input w-full"
            >
              <option value="all">Все</option>
              <option value="management_company">УК</option>
              <option value="senior_teacher">Старший педагог</option>
            </select>
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-muted-foreground">Качество</span>
            <select
              value={qualityFilter}
              onChange={event => setUrlFilter('quality', event.target.value)}
              className="input w-full"
            >
              <option value="all">Все</option>
              <option value="high">80% и выше</option>
              <option value="medium">50% - 79.99%</option>
              <option value="low">Ниже 50%</option>
            </select>
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-muted-foreground">Поиск</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                className="input w-full pl-9"
              />
            </div>
          </label>
        </div>
      </Card>

      <section className="overflow-hidden rounded-card border border-border bg-card shadow-card">
        <div className="flex flex-col gap-2 border-b border-border bg-muted/80 px-4 py-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Результаты</h2>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>Отсмотров: <strong className="tabular-nums text-foreground">{filteredReviews.length}</strong></span>
            {teacherFilter ? (
              <span>Последний: <strong className="tabular-nums text-foreground">{formatDateOnly(latestFilteredReviewDate)}</strong></span>
            ) : (
              <>
                <span>Педагогов: <strong className="tabular-nums text-foreground">{uniqueCoaches}</strong></span>
                <button
                  type="button"
                  onClick={() => setUrlFilter('quality', qualityFilter === 'low' ? 'all' : 'low')}
                  className={`font-medium transition-colors hover:underline ${lowScoreCount > 0 ? 'text-error' : 'text-foreground'}`}
                  title={lowScoreCount > 0 ? 'Показать отсмотры с качеством ниже 50%' : undefined}
                >
                  Ниже 50%: {lowScoreCount}
                </button>
              </>
            )}
          </div>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            Загружаем отсмотры...
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {teacherFilter
              ? (teacherTimeScope === 'all' ? 'По этому педагогу пока нет отсмотров.' : 'По этому педагогу нет отсмотров за выбранный период.')
              : 'Сохранённых отсмотров пока нет.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1340px] table-fixed border-collapse text-sm">
              <thead className="tg-table-header text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-56 px-4 py-3 text-left font-semibold">Студия</th>
                  <th className="w-44 px-3 py-3 text-left font-semibold">Педагог</th>
                  <th className="w-44 px-3 py-3 text-left font-semibold">Направление</th>
                  <th className="w-36 px-3 py-3 text-left font-semibold">Источник</th>
                  <th className="w-20 px-3 py-3 text-right font-semibold">%</th>
                  <th className="w-40 px-3 py-3 text-left font-semibold">Дата/время тренировки</th>
                  <th className="w-40 px-3 py-3 text-left font-semibold">Дата/время отсмотра</th>
                  <th className="w-44 px-3 py-3 text-left font-semibold">Кто отсмотрел</th>
                  <th className="w-72 px-3 py-3 text-left font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {pagedGroupedReviews.map(group => (
                  <Fragment key={group.monthKey}>
                    <tr className="border-y border-border bg-muted/50">
                      <td colSpan={9} className="px-4 py-2 text-sm font-semibold">
                        {group.monthLabel}
                      </td>
                    </tr>
                    {group.rows.map(review => {
                      const studioMeta = review.studioInternalId == null
                        ? null
                        : (studios.find(studio => studio.studio_internal_id === review.studioInternalId) ?? null)
                      const studioNumber = studioMeta?.studio_number ? String(studioMeta.studio_number) : '-'
                      const studioAddress = review.studioAddress.trim() || '-'
                      const reviewerName = review.reviewerDisplayName.trim() || '-'

                      return (
                        <tr key={review.reviewId} className="border-b border-border align-top last:border-b-0">
                          <td className="w-56 px-4 py-3">
                            <div className="font-medium">Студия {studioNumber}</div>
                            <div className="text-xs text-muted-foreground">{review.cityName || '-'}</div>
                            <div className="text-xs text-muted-foreground">{studioAddress}</div>
                          </td>
                          <td className="px-3 py-3 font-medium">{review.coachName || '-'}</td>
                          <td className="px-3 py-3">{review.directionName || '-'}</td>
                          <td className="px-3 py-3">
                            <span className="inline-flex rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                              {review.reviewSource === 'senior_teacher' ? 'Старший педагог' : 'УК'}
                            </span>
                          </td>
                          <td className={`px-3 py-3 text-right font-semibold tabular-nums ${getPercentTone(review.qualityPercent)}`}>
                            {formatPercent(review.qualityPercent)}
                          </td>
                          <td className="px-3 py-3 tabular-nums">{formatDateTime(review.trainingAt)}</td>
                          <td className="px-3 py-3 tabular-nums">{formatDateTimeFromTimestamp(review.createdAt)}</td>
                          <td className="px-3 py-3">{reviewerName}</td>
                          <td className="w-72 px-3 py-3">
                            <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
                              {isPedagogySpecialist && teacherFilter ? (
                                <button
                                  type="button"
                                  onClick={() => navigate(`${pedagogyPaths.reviewReport(review.reviewId)}?returnTo=${encodeURIComponent(returnTo)}`)}
                                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-muted"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  Открыть
                                </button>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => navigate(
                                      isPedagogySpecialist
                                        ? `${pedagogyPaths.reviewNew}?entryKey=${encodeURIComponent(review.reviewId)}&returnTo=${encodeURIComponent(returnTo)}`
                                        : `${createTrainerChecklistReviewLink(review.reviewId)}&returnTo=${encodeURIComponent(returnTo)}`
                                    )}
                                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-muted"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Открыть
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openReportWithReturn(review.reviewId, returnTo)}
                                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-muted"
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    Отчёт
                                  </button>
                                </>
                              )}
                              {canManagePedagogy && !isPedagogySpecialist && (
                                <button
                                  type="button"
                                  onClick={() => void handleArchiveReview(review)}
                                  disabled={archivingReviewId === review.reviewId}
                                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-error/30 bg-background px-3 py-2 text-xs font-medium text-error hover:bg-error/5 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  {archivingReviewId === review.reviewId ? 'Удаляем...' : 'Удалить'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filteredReviews.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-border bg-background px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Показано {formatNumber((safePage - 1) * pageSize + 1)}-{formatNumber(Math.min(safePage * pageSize, filteredReviews.length))} из {formatNumber(filteredReviews.length)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Показывать</span>
                <select
                  value={pageSize}
                  onChange={event => setUrlFilter('pageSize', event.target.value, '20')}
                  className="input h-8 w-[78px] px-2 py-1 text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
              <Button size="sm" variant="outline" onClick={() => setUrlFilter('page', String(Math.max(1, safePage - 1)), '1')} disabled={safePage <= 1}>
                Назад
              </Button>
              <div className="min-w-[88px] text-center text-sm text-muted-foreground">
                {safePage} / {pageCount}
              </div>
              <Button size="sm" variant="outline" onClick={() => setUrlFilter('page', String(Math.min(pageCount, safePage + 1)), '1')} disabled={safePage >= pageCount}>
                Далее
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

function openReportWithReturn(reviewId: string, returnTo: string) {
  openTrainerChecklistReport(reviewId, returnTo)
}
